/**
 * HTML shell color palette — respects prefers-color-scheme.
 *
 * Scope: only the HTML/CSS chrome (loading screen, fallback page, page bg,
 * transcript overlay). The 3D scene + in-scene panels stay dark always.
 *
 * Reactive: subscribes to OS-level theme changes via matchMedia.
 */

import { useEffect, useState, useMemo } from 'react'
import { palette } from '../scene'

export type ColorScheme = 'light' | 'dark'

/** Returns the user's current OS color-scheme preference, reactive to changes. */
export function useColorScheme(): ColorScheme {
  const [scheme, setScheme] = useState<ColorScheme>(() => {
    if (typeof window === 'undefined') return 'dark'
    return window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark'
  })

  useEffect(() => {
    if (typeof window === 'undefined') return
    const mq = window.matchMedia('(prefers-color-scheme: light)')
    const onChange = (e: MediaQueryListEvent) => setScheme(e.matches ? 'light' : 'dark')
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  }, [])

  return scheme
}

/** Colors used by the HTML chrome only. 3D scene colors live in `palette` directly. */
export interface ShellPalette {
  scheme: ColorScheme
  pageBackground: string
  textPrimary: string
  textSecondary: string
  textTertiary: string
  glassBackground: string
  glassBorder: string
  progressTrack: string
  link: string
  brandPrimary: string
  brandSuccess: string
}

const DARK_SHELL: Omit<ShellPalette, 'scheme'> = {
  pageBackground: palette.background.deep,
  textPrimary: 'rgba(255, 255, 255, 0.95)',
  textSecondary: 'rgba(255, 255, 255, 0.70)',
  textTertiary: 'rgba(255, 255, 255, 0.50)',
  glassBackground: palette.glass.background,
  glassBorder: 'rgba(255, 255, 255, 0.06)',
  progressTrack: 'rgba(255, 255, 255, 0.10)',
  link: '#8ab4f8',
  brandPrimary: palette.primary[500],
  brandSuccess: palette.success[500],
}

const LIGHT_SHELL: Omit<ShellPalette, 'scheme'> = {
  pageBackground: '#fafafa',
  textPrimary: 'rgba(0, 0, 0, 0.92)',
  textSecondary: 'rgba(0, 0, 0, 0.65)',
  textTertiary: 'rgba(0, 0, 0, 0.45)',
  glassBackground: 'rgba(255, 255, 255, 0.85)',
  glassBorder: 'rgba(0, 0, 0, 0.08)',
  progressTrack: 'rgba(0, 0, 0, 0.08)',
  link: '#1a5fd6',           // darker blue for AA contrast on white
  brandPrimary: palette.primary[500],
  brandSuccess: '#3d8a52',   // darker green for AA contrast on white
}

export function useShellPalette(): ShellPalette {
  const scheme = useColorScheme()
  return useMemo(
    () => ({ scheme, ...(scheme === 'light' ? LIGHT_SHELL : DARK_SHELL) }),
    [scheme],
  )
}
